import { CoObjectTypeEnum } from "../../api/models";
import { AdvancedDocumentMetadata } from "../../utils/helpers/DocumentAdvancedCustomXmlHelper";
import { DocumentParameters } from "../document/ParametersDocument";
import { Dataset } from "../../api/models/dataset";
import { AnalyticalWorkspace } from "../../api/models/analytical-workspace";
import { DatasetField } from "../../api/models/dataset-field";
import { Process } from "../../api/models/process";
import { FormatTypeEnum } from "../../api/models/format-type-enum";
import { ReferringScenariosEnum } from "../../api/models/ReferringScenarios";

export interface CoObjectItem {
  documentId: string;
  contentControlId?: string;
  id?: string;
  info: CoObjectGeneralInfo;
  source?: CoObjectSource;
  parameters?: CoObjectParameters | DocumentParameters[];
  advanced?: AdvancedDocumentMetadata;
  analyticalWorkspace?: AnalyticalWorkspace;
  dataset?: Dataset;
  amount?: DatasetField;
  isLocked?: boolean;
  inheritFormat?: boolean;
  formatType?: FormatTypeEnum;
}

export interface DBApplication {
  uid?: string;
  id: string;
  desc: string;
}

export interface FormTable {
  code: string;
  desc: string;
  oid: string;
  oidPath: string[];
}

export interface SheetForm {
  uid: string;
  code: string;
  desc: string;
}

export interface AreaChartSheet {
  name: string;
}

export interface CoObjectGeneralInfo {
  objectName: string;
  objectType: CoObjectType;
  info: string;
  overrideData?: boolean;
  overrideStyle?: boolean;
  isComponent?: boolean;
  componentReference?: ComponentReference;
}

export interface ComponentReference {
  componentLogicId: string;
  componentName: string;
  componentDocumentName: string;
}

export type CoObjectType = keyof typeof CoObjectTypeEnum;
export type CoObjectElabResultType = "HTML" | "STRINGBASE64" | "IMAGE_STRINGBASE64" | "REF_URL" | "TEXT";

export interface CoObjectSource {
  type:
    | "TABLE"
    | "DROPDOWN_LIST"
    | "DOCUMENT_PART_INCLUDE"
    | "AMOUNT"
    | "AMOUNT_AIH"
    | "DIMENSION_ATTRIBUTE"
    | "TGK_TEXT";
  elabResultType?: CoObjectElabResultType;
  choice?: string;
}

export type CoObjectParameters = DimensionDetails[];

export interface TgkTableObjectSource extends CoObjectSource {
  type: "TABLE";
  objectSource: string;
  dbApplication: DBApplication;
  form: FormTable;
  sheet: SheetForm;
  area: AreaChartSheet;
  chart: AreaChartSheet;
  path: string[];
  inheritFormatImage?: boolean;
  imageExtension?: string;
  imageResolution?: number;
}

export interface DimensionValue {
  oidPath: string[];
  oid: string;
  desc: string;
  ref?: ReferringScenariosEnum;
  code: string;
}

export interface DimensionHierarchy {
  oid: string;
  desc: string;
  code: string;
}

export interface DimensionDetails {
  dimensionOid: string;
  hierarchy?: DimensionHierarchy;
  values: DimensionValue[]; //oidPath does not include hierarchy; oid is also the last item in oidPath: string[]
  attributeCode?: string;
  structureCode?: string;
  code?: string;
  label?: string;
  type?: string;
  inherited: boolean;
}

export interface TgkAmountObjectSource extends CoObjectSource {
  type: "AMOUNT" | "AMOUNT_AIH";
  form?: FormTable;
  sheet?: SheetForm;
  area?: AreaChartSheet;
  cell?: string;
  objectSource: string;
  dbApplication?: DBApplication;
  dimensions?: DimensionDetails[];
  analyticalWorkspace?: AnalyticalWorkspace;
  dataset?: Dataset;
  amount?: DatasetField;
  process?: Process;
}

export interface TgkDropDownSource extends CoObjectSource {
  type: "DROPDOWN_LIST";
  dropDownList: TgkDropDownItem[];
}

export interface TgkDimensionAttributeSource extends CoObjectSource {
  type: "DIMENSION_ATTRIBUTE";
  dbApplication: DBApplication;
  dimensions: DimensionDetails[];
  locale: string;
}

export interface TgkTextSource {
  type: "TGK_TEXT";
  dbApplication: DBApplication;
  analyticalWorkspace: AnalyticalWorkspace;
  dataset: Dataset;
  richText: DatasetField;
  dimensions: DimensionDetails[];
  process: Process;
}

export const isObjectUpdatable = (coObjectItem: CoObjectItem): boolean => {
  let objectType = coObjectItem.info.objectType;
  return (
    objectType === "TABLE" ||
    objectType === "CHART" ||
    objectType === "AMOUNT" ||
    objectType === "DIMENSION_ATTRIBUTE" ||
    objectType === "TGK_TEXT" ||
    objectType === "DOCUMENT_PART_INCLUDE"
  );
};

export interface DocumentPartSource extends CoObjectSource {
  type: "DOCUMENT_PART_INCLUDE";
  path: string;
  includedDocumentPartId?: string;
}

export interface TgkDropDownItem {
  itemId: number;
  key: string;
  value: string;
  inEdit: boolean;
  keyError: string | undefined;
  valueError: string | undefined;
  isBlurredKey: boolean;
  isBlurredValue: boolean;
}
